import type { History } from 'history';
import { find, includes, isNil, isUndefined } from 'lodash';

import type { AuthUser } from 'common/auth/types';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { UserSubType } from 'common/graphql/types';
import type { SiteStudy } from 'common/types/site';

export const DEFAULT_PATH = '/';

/**
 * IF User opened a deep link, navigate to deep link. [Deep Link > last Saved Page]
 * Redirects to last saved URL if user has one.
 *  Else redirects to targetPath under following conditions
 *      If user dont have access to the saved study site anymore
 * @param targetPath '/' if normal login & '<PATH>' if Deep Links
 * @param activeUser Active User
 * @param updateActiveStudySite redux dispatcher to update active study and site
 * @param history hisory for url redirection
 * @returns void
 */
export function redirectToLastPagesOpened(
    targetPath: string,
    activeUser: AuthUser,
    updateActiveStudySite: (SiteStudy: SiteStudy) => void,
    history: History
): void {
    const lastOpenedData = activeUser.lastPagesOpened;
    if (isNil(activeUser) || targetPath !== DEFAULT_PATH || isNil(lastOpenedData) || !lastOpenedData.length) {
        history.push(targetPath);

        return;
    }

    // If User Logs in normally, without deep link
    const { studyId, siteId, url } = lastOpenedData[0]; // [0] -> For now we resume only one session

    if (!isSiteRoleBySubType(activeUser.subType)) {
        // Navigate to last URL - Other Users
        history.push(url);

        return;
    }

    const userstudies = activeUser?.userStudies || [];
    const userSelectedStudy = find(userstudies, { studyId });
    const usersites = userSelectedStudy?.userStudySites || [];
    const userSelectedSite = find(usersites, { siteId });

    if (isUndefined(userSelectedStudy) || isUndefined(userSelectedSite)) {
        history.push(targetPath);

        return;
    }

    const siteStudy: SiteStudy = {
        site: userSelectedSite,
        study: userSelectedStudy,
    };
    updateActiveStudySite(siteStudy);
    // Navigate to last URL - SITE or MONITOR users
    history.push(url);
}

export const isSecurityQuestionMissing = (activeUser: AuthUser): boolean =>
    isNil(activeUser.securityQuestion1Text) ||
    isNil(activeUser.securityQuestion2Text) ||
    isNil(activeUser.securityQuestion3Text);

const getSiteSubTypes = () => {
    return [
        UserSubType.SITE,
        UserSubType.MONITOR,
        UserSubType.DATA_MANAGER,
        UserSubType.INSPECTOR,
        UserSubType.DATA_CHANGE_SUPPORT,
        ...(!featureFlags.fxIntegration ? [UserSubType.PRODUCT_SUPPORT] : []),
    ];
};

export const isSiteRoleBySubType = (subType: UserSubType) => includes(getSiteSubTypes(), subType);
